import React from "react";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FeatureCard } from "../brain/FeatureCard";
import { FAQAccordion } from "./FAQAccordion";
import { technologyFeatures } from "@/config/technology";
import { 
  Brain, 
  Shield, 
  Clock, 
  Users, 
  Database, 
  Stethoscope,
  Headphones,
  Cpu,
  Lock
} from "lucide-react";

export const TabbedTechnologyContent = () => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      <Tabs defaultValue="how-it-works" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-12 bg-white/10 p-1 rounded-xl border border-white/20">
          <TabsTrigger value="how-it-works" className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">How It Works</TabsTrigger>
          <TabsTrigger value="core-technology" className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">Core Technology</TabsTrigger>
          <TabsTrigger value="security" className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">Security & Privacy</TabsTrigger>
          <TabsTrigger value="implementation" className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">Implementation</TabsTrigger>
          <TabsTrigger value="faqs" className="text-white data-[state=active]:bg-white/20 data-[state=active]:text-white">FAQs</TabsTrigger>
        </TabsList>

        <TabsContent value="how-it-works" className="space-y-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="call-process">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Headphones className="h-5 w-5 text-primary" />
                  The Journey from Call to Care
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Every healthcare conversation follows a carefully orchestrated process that transforms routine interactions into powerful health insights.</p>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="group p-6 rounded-xl surface-elevated">
                      <h4 className="font-bold text-white mb-3 group-hover:text-primary transition-colors">1. Conversation Capture</h4>
                      <p className="text-gray-200 leading-relaxed">Real-time audio processing begins the moment a call starts, using advanced noise reduction and speech enhancement.</p>
                    </div>
                    <div className="group p-6 rounded-xl surface-elevated">
                      <h4 className="font-bold text-white mb-3 group-hover:text-primary transition-colors">2. Voice Analysis</h4>
                      <p className="text-gray-200 leading-relaxed">Our AI extracts hundreds of speech biomarkers including vocal tremor, pauses, articulation, and emotional indicators.</p>
                    </div>
                    <div className="group p-6 rounded-xl surface-elevated">
                      <h4 className="font-bold text-white mb-3 group-hover:text-primary transition-colors">3. Clinical Correlation</h4>
                      <p className="text-gray-200 leading-relaxed">Machine learning models trained on clinical datasets correlate speech patterns with health conditions and cognitive states.</p>
                    </div>
                    <div className="group p-6 rounded-xl surface-elevated">
                      <h4 className="font-bold text-white mb-3 group-hover:text-primary transition-colors">4. Actionable Insights</h4>
                      <p className="text-gray-200 leading-relaxed">Healthcare providers receive real-time alerts and comprehensive reports to inform care decisions.</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="ai-processing">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Brain className="h-5 w-5 text-primary" />
                  AI Processing Pipeline
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Our sophisticated AI pipeline processes voice data through multiple layers of analysis to extract meaningful health insights.</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 border-l-4 border-primary bg-muted/20">
                      <Cpu className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h5 className="font-semibold text-white">Signal Processing</h5>
                        <p className="text-sm">Advanced algorithms clean and normalize audio signals, removing background noise and enhancing speech clarity.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 border-l-4 border-primary bg-muted/20">
                      <Database className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h5 className="font-semibold text-white">Feature Extraction</h5>
                        <p className="text-sm">Extraction of over 2500 speech biomarkers in real-time including prosodic features, vocal dynamics, and linguistic patterns.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 border-l-4 border-primary bg-muted/20">
                      <Stethoscope className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <h5 className="font-semibold text-white">Clinical Analysis</h5>
                        <p className="text-sm">Machine learning models trained on clinical datasets identify patterns associated with various health conditions.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        <TabsContent value="core-technology" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {technologyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <FeatureCard
                  {...feature}
                  className="h-full hover:shadow-xl transition-shadow duration-300"
                />
              </motion.div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="data-protection">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-primary" />
                  Data Protection & Privacy
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Our platform maintains the highest standards of data security and patient privacy, ensuring full compliance with healthcare regulations.</p>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">HIPAA Compliance</h4>
                      <p className="text-sm">Full compliance with HIPAA regulations including encryption, access controls, and audit trails.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">End-to-End Encryption</h4>
                      <p className="text-sm">All data is encrypted in transit and at rest using AES-256 encryption standards.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">Access Controls</h4>
                      <p className="text-sm">Role-based access controls ensure only authorized personnel can access patient data.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">Audit Logging</h4>
                      <p className="text-sm">Comprehensive audit trails track all data access and system interactions.</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="compliance">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-primary" />
                  Regulatory Compliance
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Our technology meets stringent regulatory requirements across multiple jurisdictions and healthcare standards.</p>
                  
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      FDA guidelines for digital health technologies
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      GDPR compliance for European operations
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      SOC 2 Type II certification
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary"></div>
                      ISO 27001 information security standards
                    </li>
                  </ul>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        <TabsContent value="implementation" className="space-y-6">
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="technical-requirements">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Database className="h-5 w-5 text-primary" />
                  Technical Requirements
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Our platform is designed for seamless integration with existing healthcare infrastructure.</p>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">Minimum Requirements</h4>
                      <ul className="text-sm space-y-1">
                        <li>• Broadband internet connection (10 Mbps)</li>
                        <li>• Modern web browser (Chrome, Firefox, Safari)</li>
                        <li>• Audio input/output capabilities</li>
                        <li>• Compatible telephony system</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30">
                      <h4 className="font-semibold text-white mb-2">Integration Options</h4>
                      <ul className="text-sm space-y-1">
                        <li>• RESTful API integration</li>
                        <li>• EHR system connectors</li>
                        <li>• SIP/VoIP compatibility</li>
                        <li>• Custom webhook support</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="deployment">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-primary" />
                  Deployment Timeline
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Typical deployment follows a structured approach to ensure smooth implementation and user adoption.</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">1</div>
                      <div>
                        <h5 className="font-semibold text-white">Discovery & Planning (Week 1-2)</h5>
                        <p className="text-sm">Requirements gathering, system assessment, and integration planning.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">2</div>
                      <div>
                        <h5 className="font-semibold text-white">Configuration & Testing (Week 3-4)</h5>
                        <p className="text-sm">System configuration, integration setup, and comprehensive testing.</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">3</div>
                      <div>
                        <h5 className="font-semibold text-white">Training & Launch (Week 5-6)</h5>
                        <p className="text-sm">User training, pilot deployment, and full system launch.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="support">
              <AccordionTrigger className="text-left text-white">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-primary" />
                  Ongoing Support
                </div>
              </AccordionTrigger>
              <AccordionContent className="prose prose-lg text-gray-200 max-w-none">
                <div className="space-y-4">
                  <p>Comprehensive support ensures optimal performance and continuous improvement of your voice AI implementation.</p>
                  
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="p-4 border rounded-lg bg-muted/30 text-center">
                      <h4 className="font-semibold text-white mb-2">24/7 Monitoring</h4>
                      <p className="text-sm">Continuous system monitoring and proactive issue resolution.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30 text-center">
                      <h4 className="font-semibold text-white mb-2">Expert Support</h4>
                      <p className="text-sm">Direct access to technical experts and clinical specialists.</p>
                    </div>
                    <div className="p-4 border rounded-lg bg-muted/30 text-center">
                      <h4 className="font-semibold text-white mb-2">Regular Updates</h4>
                      <p className="text-sm">Continuous platform improvements and new feature releases.</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>

        <TabsContent value="faqs" className="space-y-6">
          <FAQAccordion />
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};